import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Minus, Plus, X, ShoppingBag } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useLocale } from "@/context/LocaleContext";
import { formatPrice } from "@/lib/format";

export default function CartDrawer() {
  const { items, isOpen, setIsOpen, updateQuantity, removeItem, pricing, loading, pricingError, refreshPricing } = useCart();
  const { currency, t } = useLocale();
  const navigate = useNavigate();

  const displayCurrency = pricing?.currency || currency;
  const threshold = pricing?.free_shipping_threshold ?? 50;
  const subtotal = pricing?.subtotal || 0;
  const remaining = Math.max(0, threshold - (subtotal - (pricing?.discount || 0)));
  const progressPct = threshold === 0 ? 100 : Math.min(100, ((subtotal - (pricing?.discount || 0)) / threshold) * 100);
  const money = (value) => loading || !pricing || value == null ? "—" : formatPrice(value, displayCurrency);

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent side="right" className="bg-levami-creme border-levami-sable w-full sm:max-w-md flex flex-col p-0" data-testid="cart-drawer">
        <SheetHeader className="p-6 border-b border-levami-sable">
          <SheetTitle className="font-serif-heading text-xl text-levami-charbon">{t("cart.title")}</SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
            <ShoppingBag className="h-10 w-10 text-levami-brun/30 mb-4" />
            <p className="text-levami-brun/70 mb-6">{t("cart.empty")}</p>
            <Button onClick={() => setIsOpen(false)} asChild className="bg-levami-charbon text-levami-creme hover:bg-levami-brun">
              <Link to="/boutique">{t("cart.discover")}</Link>
            </Button>
          </div>
        ) : (
          <>
            {pricing && !loading && !pricingError && <div className="px-6 pt-4" data-testid="cart-shipping-progress">
              {remaining > 0 ? (
                <p className="text-xs text-levami-brun mb-2">
                  {t("cart.remaining", { amount: formatPrice(remaining, displayCurrency) })}
                </p>
              ) : (
                <p className="text-xs text-levami-olive mb-2">{t("cart.free_shipping_included")} ✨</p>
              )}
              <Progress value={progressPct} className="h-1.5 bg-levami-sable [&>div]:bg-levami-or" />
            </div>}

            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              {items.map((item) => {
                const lineItem = pricing?.line_items?.find((li) => li.variant_id === item.variant_id);
                return (
                  <div key={item.variant_id} className="flex gap-3 pb-4 border-b border-levami-sable/60" data-testid="cart-line-item">
                    <div className="w-16 h-20 rounded-md overflow-hidden bg-levami-ivoire shrink-0">
                      {item.image_url && <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-levami-charbon font-medium truncate">{item.name}</p>
                      <p className="text-xs text-levami-brun/60">{item.color} {item.size ? `· ${item.size}` : ""}</p>
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center gap-1 border border-levami-sable rounded-md">
                          <Button variant="ghost" size="icon" className="h-8 w-8" disabled={item.quantity <= 1} aria-label={t("b2b.decrease_quantity")} onClick={() => updateQuantity(item.variant_id, item.quantity - 1)} data-testid="cart-decrease-qty">
                            <Minus className="h-3 w-3" />
                          </Button>
                          <Input readOnly value={item.quantity} aria-label={t("b2b.col_qty")} className="h-8 w-8 text-center border-0 p-0" />
                          <Button variant="ghost" size="icon" className="h-8 w-8" aria-label={t("b2b.increase_quantity")} onClick={() => updateQuantity(item.variant_id, item.quantity + 1)} data-testid="cart-increase-qty">
                            <Plus className="h-3 w-3" />
                          </Button>
                        </div>
                        <span className="text-sm text-levami-charbon tabular-nums">{money(lineItem?.line_total)}</span>
                      </div>
                    </div>
                    <button onClick={() => removeItem(item.variant_id)} aria-label={t("cart.remove_item")} data-testid="cart-remove-item" className="text-levami-brun/40 hover:text-levami-primary">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                );
              })}
            </div>

            <div className="p-6 border-t border-levami-sable space-y-3">
              {pricing?.coupon_code && (
                <p className="text-xs text-levami-olive">{t("cart.applied_code", { code: pricing.coupon_code })}</p>
              )}

              <div className="space-y-1 text-sm">
                <div className="flex justify-between text-levami-brun">
                  <span>{t("cart.subtotal")}</span>
                  <span className="tabular-nums">{money(pricing?.subtotal)}</span>
                </div>
                {pricing?.discount > 0 && (
                  <div className="flex justify-between text-levami-olive">
                    <span>{t("cart.discount")}</span>
                    <span className="tabular-nums">-{formatPrice(pricing.discount, displayCurrency)}</span>
                  </div>
                )}
                <div className="flex justify-between text-levami-brun">
                  <span>{t("cart.shipping")}</span>
                  <span className="tabular-nums">{!pricing || loading ? "—" : pricing.shipping_cost === 0 ? t("cart.free") : money(pricing.shipping_cost)}</span>
                </div>
                <div className="flex justify-between text-levami-charbon font-medium text-base pt-2 border-t border-levami-sable mt-2">
                  <span>{t("cart.total")}</span>
                  <span className="tabular-nums">{money(pricing?.total)}</span>
                </div>
              </div>
              {loading && <p className="text-xs" role="status">{t("common.loading")}</p>}
              {pricingError && <div className="text-sm space-y-2" role="alert" data-testid="cart-drawer-pricing-error"><p>{pricingError}</p><Button variant="outline" size="sm" onClick={() => refreshPricing()}>{t("b2b.retry")}</Button></div>}

              <Button
                className="w-full bg-levami-charbon text-levami-creme hover:bg-levami-brun h-12 mt-2"
                onClick={() => { setIsOpen(false); navigate("/panier"); }}
                data-testid="cart-checkout-button"
              >
                {t("cart.view_and_pay")}
              </Button>
              <button className="w-full text-sm text-levami-brun underline underline-offset-4" onClick={() => setIsOpen(false)} data-testid="cart-continue-shopping">{t("cart.continue_shopping")}</button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
